'use client';

import { c as _c } from "react/compiler-runtime";
import type { Role } from '@svs/nova-core/nextjs/auth';
import { getFormattedCurrency } from '@svs/nova-poolbetting/core/lib/client';
import { useCouponGroupedOutcomes, useCouponId, useCouponIndices, useCouponPrice, useCouponsPrice, useCouponStatus, useCouponWallets, useHasPopulatedCoupon } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { useEngine } from '@svs/nova-poolbetting/core/nextjs/engine';
import { useBalance } from '@svs/nova-poolbetting/core/nextjs/lib/client';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { RetailerBalanceResult } from '@svs/nova-poolbetting/schema/marketplace';
import { usePathname, useSearchParams } from 'next/navigation';
import { type ReactNode, useMemo } from 'react';
import { type InjectedWalletProps, withContextWallet } from '@/components/index.browser';
import BetslipStandard from './BetslipStandard';
type BetslipStandardClientContainerProps = {
  betslipContent: ReactNode;
  drawNumber: DrawNumber;
  retailerData?: RetailerBalanceResult;
  product: Definition['drawProducts'][number];
  role: Role;
};
type BetslipStandardClientContainerPropsWithInjectedWallet = BetslipStandardClientContainerProps & InjectedWalletProps;

/**
 * Component för standard betslip.
 *
 * @param root0 - The props object.
 * @param root0.betslipContent - The overview component to be displayed in the drawer
 * @param root0.retailerData - The retailer balance data
 * @param root0.drawNumber - The draw number for which to fetch marketplace balance
 * @param root0.product - The product for which to fetch marketplace balance
 * @param root0.contextWallets - The wallet data from context
 * @param root0.role - The role of the user (e.g., 'Retailer', 'Player')
 * @returns standard betslip component
 */
function BetslipStandardClientContainer(t0) {
  const $ = _c(51);
  const {
    betslipContent,
    drawNumber,
    retailerData,
    product,
    contextWallets,
    role
  } = t0;
  const [couponId, couponIndex] = useCouponId();
  const pathname = usePathname();
  const searchParams = useSearchParams().toString();
  const returnUrl = searchParams ? `${pathname}?${searchParams}` : pathname;
  const definition = useEngine();
  let t1;
  if ($[0] !== couponId) {
    t1 = {
      couponId
    };
    $[0] = couponId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalCouponPrice = useCouponsPrice(t1);
  let t2;
  if ($[2] !== couponId || $[3] !== couponIndex) {
    t2 = {
      couponId,
      couponIndex
    };
    $[2] = couponId;
    $[3] = couponIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const currentCouponPrice = useCouponPrice(t2);
  let t3;
  if ($[5] !== couponId) {
    t3 = {
      couponId
    };
    $[5] = couponId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const isMultiCoupon = useCouponIndices(t3).length > 1;
  let t4;
  if ($[7] !== couponId || $[8] !== couponIndex) {
    t4 = {
      couponId,
      couponIndex
    };
    $[7] = couponId;
    $[8] = couponIndex;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const couponStatus = useCouponStatus(t4);
  let t5;
  if ($[10] !== couponId || $[11] !== couponIndex) {
    t5 = {
      couponId,
      couponIndex
    };
    $[10] = couponId;
    $[11] = couponIndex;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const outcomeGroups = useCouponGroupedOutcomes(t5);
  let t6;
  if ($[13] !== couponId) {
    t6 = {
      couponId
    };
    $[13] = couponId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const activeWallets = useCouponWallets(t6);
  let t7;
  if ($[15] !== activeWallets || $[16] !== contextWallets || $[17] !== couponIndex || $[18] !== drawNumber || $[19] !== product || $[20] !== retailerData || $[21] !== role) {
    t7 = {
      activeWallets,
      couponIndex,
      drawNumber,
      retailerData,
      product,
      contextWallets,
      role
    };
    $[15] = activeWallets;
    $[16] = contextWallets;
    $[17] = couponIndex;
    $[18] = drawNumber;
    $[19] = product;
    $[20] = retailerData;
    $[21] = role;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const {
    balance,
    shouldRedirectUser
  } = useBalance(t7);
  let started = 0;
  let total = 0;
  for (const [hedges, count] of Object.entries(outcomeGroups)) {
    const numHedges = Number(hedges);
    if (numHedges !== 0) {
      started = started + count;
    }
    total = total + count;
  }
  let t8;
  if ($[23] !== started || $[24] !== total) {
    t8 = [started, total];
    $[23] = started;
    $[24] = total;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const [couponEventsStarted, totalCouponEventCount] = t8 as const;
  const fullHedgeCount = outcomeGroups[3] ?? 0;
  const halfHedgeCount = outcomeGroups[2] ?? 0;
  let t9;
  if ($[26] !== definition) {
    t9 = definition.hasAdditions();
    $[26] = definition;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const hasAdditions = t9;
  let t10;
  if ($[28] !== couponId) {
    t10 = {
      couponId
    };
    $[28] = couponId;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const isPopulatedCoupon = useHasPopulatedCoupon(t10);
  let t11;
  if ($[30] !== totalCouponPrice) {
    t11 = getFormattedCurrency(totalCouponPrice, {
      unit: "KR"
    });
    $[30] = totalCouponPrice;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== currentCouponPrice) {
    t12 = getFormattedCurrency(currentCouponPrice);
    $[32] = currentCouponPrice;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== balance) {
    t13 = getFormattedCurrency(balance, {
      shouldUseDecimals: true
    });
    $[34] = balance;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== betslipContent || $[37] !== couponEventsStarted || $[38] !== couponStatus || $[39] !== fullHedgeCount || $[40] !== halfHedgeCount || $[41] !== hasAdditions || $[42] !== isMultiCoupon || $[43] !== isPopulatedCoupon || $[44] !== returnUrl || $[45] !== shouldRedirectUser || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== totalCouponEventCount) {
    const betslipProps = {
      totalCouponPrice: t11,
      currentCouponPrice: t12,
      isMultiCoupon,
      isPopulatedCoupon,
      fullHedgeCount,
      halfHedgeCount,
      couponEventsStarted,
      couponEventCount: totalCouponEventCount,
      couponStatus,
      hasAdditions,
      betslipContent,
      balance: t13,
      shouldRedirectUser,
      returnUrl
    };
    t14 = <BetslipStandard betslipProps={betslipProps} />;
    $[36] = betslipContent;
    $[37] = couponEventsStarted;
    $[38] = couponStatus;
    $[39] = fullHedgeCount;
    $[40] = halfHedgeCount;
    $[41] = hasAdditions;
    $[42] = isMultiCoupon;
    $[43] = isPopulatedCoupon;
    $[44] = returnUrl;
    $[45] = shouldRedirectUser;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = totalCouponEventCount;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
}
export default withContextWallet<BetslipStandardClientContainerProps>(BetslipStandardClientContainer);